import { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, CheckCircle, Truck, Package, ArrowRight, Clock, Tag, FileText, AlertCircle, Download } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

const MyGuidesPage = () => {
  const { user, authAxios, refreshUser, loading: authLoading } = useAuth();
  const [balance, setBalance] = useState(null);
  const [guides, setGuides] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    carrier_id: "dhl", service_type: "standard", origin_cp: "", destination_cp: "", weight: "1", recipient_name: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [balanceRes, guidesRes, txRes] = await Promise.all([
        authAxios.get("/guides/balance"),
        authAxios.get("/guides/my-guides"),
        authAxios.get("/guides/transactions")
      ]);
      setBalance(balanceRes.data);
      setGuides(guidesRes.data);
      setTransactions(txRes.data);
    } catch (error) { /* error handled */; } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const response = await authAxios.post("/guides/generate", generateForm);
      toast.success(`Guía generada: ${response.data.tracking_number}`);
      setShowGenerate(false);
      setGenerateForm({ carrier_id: "dhl", service_type: "standard", origin_cp: "", destination_cp: "", weight: "1", recipient_name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar guía");
    } finally { setGenerating(false); }
  };

  if (authLoading) return <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6] flex items-center justify-center">Cargando...</div>;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="my-guides-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#5D4037]">Mis Guías Prepago</h1>
            <p className="text-stone-600">Gestiona y genera tus guías de envío</p>
          </div>
          <div className="flex gap-3">
            <Link to="/guias-prepago" className="btn-secondary flex items-center gap-2 text-sm" data-testid="buy-more-guides-btn">
              <CreditCard size={16} /> Comprar más guías
            </Link>
            {balance?.available_guides > 0 && (
              <button onClick={() => setShowGenerate(true)} className="btn-primary flex items-center gap-2 text-sm" data-testid="generate-guide-btn">
                <FileText size={16} /> Generar Guía
              </button>
            )}
          </div>
        </div>

        {/* Balance Card */}
        {!loading && balance && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bento-card p-6 bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] text-white" data-testid="guide-balance-card">
              <p className="text-sm text-white/70">Guías disponibles</p>
              <p className="text-5xl font-black mt-2">{balance.available_guides}</p>
              <p className="text-xs text-white/60 mt-2">Sin fecha de vencimiento</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bento-card p-6">
              <p className="text-sm text-stone-500">Guías usadas</p>
              <p className="text-3xl font-black text-[#5D4037] mt-2">{balance.used_guides}</p>
              <p className="text-xs text-stone-400 mt-2">Total generadas</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bento-card p-6">
              <p className="text-sm text-stone-500">Total compradas</p>
              <p className="text-3xl font-black text-[#5D4037] mt-2">{balance.total_guides}</p>
              <p className="text-xs text-stone-400 mt-2">Histórico</p>
            </motion.div>
          </div>
        )}

        {/* Generate Guide Modal */}
        {showGenerate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
              <h3 className="text-lg font-bold text-[#5D4037] mb-4">Generar Guía de Envío</h3>
              <form onSubmit={handleGenerate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#5D4037] mb-1">Paquetería</label>
                    <select value={generateForm.carrier_id} onChange={(e) => setGenerateForm({...generateForm, carrier_id: e.target.value})} className="w-full h-12 px-3 rounded-xl border border-stone-200 outline-none bg-white" data-testid="gen-carrier-select">
                      <option value="dhl">DHL Express</option>
                      <option value="fedex">FedEx</option>
                      <option value="ups">UPS</option>
                      <option value="estafeta">Estafeta</option>
                      <option value="redpack">Redpack</option>
                      <option value="paquetexpress">Paquetexpress</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#5D4037] mb-1">Servicio</label>
                    <select value={generateForm.service_type} onChange={(e) => setGenerateForm({...generateForm, service_type: e.target.value})} className="w-full h-12 px-3 rounded-xl border border-stone-200 outline-none bg-white" data-testid="gen-service-select">
                      <option value="express">Express (1-2 días)</option>
                      <option value="standard">Standard (3-5 días)</option>
                      <option value="economico">Económico (5-7 días)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#5D4037] mb-1">CP Origen</label>
                    <input type="text" value={generateForm.origin_cp} onChange={(e) => setGenerateForm({...generateForm, origin_cp: e.target.value.replace(/\D/g, '').slice(0, 5)})} maxLength={5} placeholder="06600" className="w-full h-12 px-3 rounded-xl border border-stone-200 outline-none font-mono" required data-testid="gen-origin-cp" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#5D4037] mb-1">CP Destino</label>
                    <input type="text" value={generateForm.destination_cp} onChange={(e) => setGenerateForm({...generateForm, destination_cp: e.target.value.replace(/\D/g, '').slice(0, 5)})} maxLength={5} placeholder="44100" className="w-full h-12 px-3 rounded-xl border border-stone-200 outline-none font-mono" required data-testid="gen-dest-cp" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#5D4037] mb-1">Peso (kg)</label>
                    <input type="number" value={generateForm.weight} onChange={(e) => setGenerateForm({...generateForm, weight: e.target.value})} min="0.1" step="0.1" className="w-full h-12 px-3 rounded-xl border border-stone-200 outline-none" required data-testid="gen-weight" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#5D4037] mb-1">Destinatario</label>
                    <input type="text" value={generateForm.recipient_name} onChange={(e) => setGenerateForm({...generateForm, recipient_name: e.target.value})} placeholder="Nombre" className="w-full h-12 px-3 rounded-xl border border-stone-200 outline-none" required data-testid="gen-recipient" />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowGenerate(false)} className="flex-1 h-12 border border-stone-200 rounded-xl font-medium">Cancelar</button>
                  <button type="submit" disabled={generating} className="flex-1 btn-primary h-12" data-testid="gen-submit-btn">{generating ? "Generando..." : "Generar Guía"}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Generated Guides List */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-[#5D4037] mb-4">Guías Generadas</h2>
          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : guides.length === 0 ? (
            <div className="bento-card p-12 text-center">
              <Truck size={48} className="text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No has generado guías aún</p>
              <p className="text-sm text-stone-400 mt-2">Compra un paquete y genera tu primera guía</p>
            </div>
          ) : (
            <div className="space-y-3">
              {guides.map((guide) => (
                <div key={guide.id} className="bento-card p-4 flex items-center gap-4" data-testid={`guide-${guide.id}`}>
                  <div className="w-10 h-10 bg-[#F57C00]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-[#F57C00]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-bold text-[#5D4037]">{guide.tracking_number}</p>
                    <p className="text-sm text-stone-500">{guide.carrier_name} - {guide.service_type} - {guide.origin_cp} → {guide.destination_cp}</p>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <button onClick={async () => {
                      try {
                        const r = await authAxios.get(`/guides/${guide.id}/label`);
                        const link = document.createElement("a");
                        link.href = `data:application/pdf;base64,${r.data.label_pdf}`;
                        link.download = `guia_${guide.tracking_number}.pdf`;
                        link.click();
                        toast.success("Etiqueta descargada");
                      } catch (e) { toast.error("Error al descargar etiqueta"); }
                    }} className="p-2 text-[#2E7D32] hover:bg-[#2E7D32]/10 rounded-lg" title="Descargar etiqueta PDF" data-testid={`download-label-${guide.id}`}>
                      <Download size={18} />
                    </button>
                    <div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${guide.status === "active" ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-600"}`}>
                        {guide.status === "active" ? "Activa" : guide.status}
                      </span>
                      <p className="text-xs text-stone-400 mt-1">{new Date(guide.created_at).toLocaleDateString("es-MX")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transactions */}
        <div>
          <h2 className="text-lg font-bold text-[#5D4037] mb-4">Historial de Compras</h2>
          {transactions.length === 0 ? (
            <div className="bento-card p-8 text-center">
              <CreditCard size={48} className="text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">Sin transacciones aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div key={tx.id} className="bento-card p-4 flex items-center gap-4" data-testid={`tx-${tx.id}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tx.payment_status === "paid" ? "bg-green-100" : "bg-yellow-100"}`}>
                    {tx.payment_status === "paid" ? <CheckCircle size={20} className="text-green-600" /> : <Clock size={20} className="text-yellow-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#5D4037]">{tx.description || `${tx.guides_count} guías prepago`}</p>
                    <p className="text-sm text-stone-500">{new Date(tx.created_at).toLocaleDateString("es-MX")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#2E7D32]">${tx.amount?.toLocaleString("es-MX")} MXN</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${tx.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {tx.payment_status === "paid" ? "Pagado" : "Pendiente"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyGuidesPage;
